/**
 * A2P registration row normalizers.
 *
 * The backend returns raw `a2p_registrations` rows whose columns are PascalCase
 * (e.g. `Status`, `BusinessName`), but several historical response paths returned
 * the lowercase/snake_case shape (`status`, `business_name`). These helpers
 * centralize the PascalCase || snake_case fallbacks that were previously
 * hand-written across the store actions and the wizard's created() hook, so the
 * two casings are resolved in exactly one place.
 *
 * KEEP IN SYNC with the mirror in
 * src/store/__tests__/a2p-registration-normalizers.test.js.
 */

/**
 * Normalize a registration row's status to a lowercase string.
 *
 * Prefers the PascalCase `Status` column, falls back to the legacy lowercase
 * `status`, and returns '' when the row (or both fields) is missing. Uses `??`
 * so an explicit empty-string status is preserved rather than skipped.
 *
 * This fixes the post-submit cache bug: submit/update responses carry `Status`
 * (PascalCase), so reading `.status` left the cached status `undefined` until
 * the next full status poll.
 *
 * @param {Object|null|undefined} reg A raw a2p_registrations row.
 * @returns {string} Lowercase status, or '' when unavailable.
 */
export function getRegStatus(reg) {
  if (reg === null || reg === undefined) return '';
  // Nullish-coalesce of (Status ?? status ?? ''), written WITHOUT the `??` operator:
  // the repo's pinned ESLint crashes on `??` chains in code-path analysis, and an
  // explicit empty-string Status must be PRESERVED (a `||` fallback would wrongly
  // fall through to `status`). This branch chain is exactly `Status ?? status ?? ''`.
  let s = reg.Status;
  if (s === null || s === undefined) s = reg.status;
  if (s === null || s === undefined) s = '';
  return String(s).toLowerCase();
}

/**
 * Map a raw a2p_registrations row to the wizard's flat form shape.
 *
 * Applies the same PascalCase || snake_case || default fallbacks the wizard's
 * created() hook used inline. A null/undefined row yields the all-defaults form
 * (country 'US', messageFrequency 'Up to 8 msgs/month', everything else '').
 *
 * NOTE: intentionally does NOT include a `documents` key — document hydration
 * (parsing DocumentsUploaded JSON) is handled separately by the wizard.
 *
 * @param {Object|null|undefined} reg A raw a2p_registrations row.
 * @returns {Object} The wizard form object.
 */
export function regToForm(reg) {
  const r = reg || {};
  return {
    businessName: r.BusinessName || r.business_name || '',
    businessAddress: r.BusinessAddress || r.business_address || '',
    ein: r.EIN || r.ein || '',
    businessType: r.BusinessType || r.business_type || '',
    website: r.Website || r.website || '',
    country: r.Country || r.country || 'US',
    repName: r.RepName || r.rep_name || '',
    repTitle: r.RepTitle || r.rep_title || '',
    repEmail: r.RepEmail || r.rep_email || '',
    repPhone: r.RepPhone || r.rep_phone || '',
    brandDescription: r.BrandDescription || r.brand_description || '',
    sampleMessage: r.SampleMessage || r.sample_message || '',
    optInOut: r.OptInOutProcedures || r.opt_in_out || '',
    privacyPolicyURL: r.PrivacyPolicyURL || r.privacy_policy_url || '',
    termsOfServiceURL: r.TermsOfServiceURL || r.terms_of_service_url || '',
    optInScreenshotURL: r.OptInScreenshotURL || r.opt_in_screenshot_url || '',
    messageFrequency: r.MessageFrequency || r.message_frequency || 'Up to 8 msgs/month',
    officePhone: r.OfficePhone || r.office_phone || '',
    officeEmail: r.OfficeEmail || r.office_email || '',
  };
}
